import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { STATUS_TRIAL } from '../enums/companyStatusEnum';

const authRoutes = [
    '/login/otp/',
    '/login',
    '/sso-login',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/activation',
    '/welcome',
    '/launchpad',
    '/logout',
];

export const useHubSpotChat = () => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [activeConversation, setActiveConversation] = useState(false);

    const eventRef = useRef(null);
    const location = useLocation();
    const companyStatus = useSelector(state => state.companyReducer?.company?.status);

    const isAuthRoute = useMemo(() => {
        return authRoutes.some(route => location.pathname.includes(route));
    }, [location.pathname]);

    const shouldRenderChatBot = useMemo(() => STATUS_TRIAL.value === companyStatus, [companyStatus]);

    const openHubSpotChat = useCallback(() => {
        if (window.HubSpotConversations && shouldRenderChatBot) {
            window.HubSpotConversations.widget.load();
        }
    }, [shouldRenderChatBot]);

    const hideHubSpotChat = useCallback(() => {
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.remove();
        }
    }, []);

    useEffect(() => {
        window.hsConversationsSettings = {
            loadImmediately: false,
            disableAttachment: true,
        };

        if (window.HubSpotConversations) {
            setHasLoaded(true);
        } else {
            window.hsConversationsOnReady = [
                () => {
                    setHasLoaded(true);
                },
            ];
        }

        return () => {
            hideHubSpotChat();
            window.hsConversationsOnReady = [];
        };
    }, [hideHubSpotChat]);

    useEffect(() => {
        eventRef.current = payload => {
            setActiveConversation(payload.conversation.conversationId);
        };

        if (hasLoaded) {
            window.HubSpotConversations.on('conversationStarted', eventRef.current);
        }

        return () => {
            if (window.HubSpotConversations) {
                window.HubSpotConversations.off('conversationStarted', eventRef.current);
            }
        };
    }, [hasLoaded]);

    useEffect(() => {
        if (isAuthRoute) {
            hideHubSpotChat();
        }
    }, [isAuthRoute, hideHubSpotChat]);

    return {
        hasLoaded,
        activeConversation,
        openHubSpotChat,
        hideHubSpotChat,
        shouldRenderChatBot,
    };
};
